import Axios from 'axios';
import { getRequestToken, onRequestTokenUpdate } from '@nextcloud/auth';
import { generateUrl } from '@nextcloud/router';

const RETRY_KEY = Symbol('csrf-retry');
const onError$2 = axios => async (error) => {
    const { config, response, request } = error;
    const responseURL = request?.responseURL;
    const status = response?.status;
    if (status === 412
        && response?.data?.message === 'CSRF check failed'
        && config[RETRY_KEY] === undefined) {
        console.warn(`Request to ${responseURL} failed because of a CSRF mismatch. Fetching a new token`);
        const { data: { token } } = await axios.get(generateUrl('/csrftoken'));
        console.debug(`New request token ${token} fetched`);
        axios.defaults.headers.requesttoken = token;
        return axios({
            ...config,
            headers: {
                ...config.headers,
                requesttoken: token,
            },
            [RETRY_KEY]: true,
        });
    }
    return Promise.reject(error);
};

const RETRY_DELAY_KEY = Symbol('retryDelay');
const onError$1 = axios => async (error) => {
    const { config, response, request } = error;
    const responseURL = request?.responseURL;
    const status = response?.status;
    const headers = response?.headers;
    /**
     * Retry requests if they failed due to maintenance mode
     *
     * The delay is exponential. It starts at 2s and then doubles
     * until a final retry after 32s. This results in roughly 1m of
     * retries until we give up and throw the axios error towards
     * the caller.
     */
    if (status === 503
        && headers['x-nextcloud-maintenance-mode'] === '1'
        && config.retryIfMaintenanceMode
        && (!config[RETRY_DELAY_KEY] || config[RETRY_DELAY_KEY] <= 32)) {
        const retryDelay = (config[RETRY_DELAY_KEY] ?? 1) * 2;
        console.warn(`Request to ${responseURL} failed because of maintenance mode. Retrying in ${retryDelay}s`);
        await new Promise((resolve, _) => {
            setTimeout(resolve, retryDelay * 1000);
        });
        return axios({
            ...config,
            [RETRY_DELAY_KEY]: retryDelay,
        });
    }
    return Promise.reject(error);
};

const onError = async (error) => {
    const { config, response, request } = error;
    const responseURL = request?.responseURL;
    const status = response?.status;
    if (status === 401
        && response?.data?.message === 'Current user is not logged in'
        && config.reloadExpiredSession
        && window?.location) {
        console.error(`Request to ${responseURL} failed because the user session expired. Reloading the page …`);
        window.location.reload();
    }
    return Promise.reject(error);
};

const client = Axios.create({
    headers: {
        requesttoken: getRequestToken() ?? ''
    }
});
const cancelableClient = Object.assign(client, {
    CancelToken: Axios.CancelToken,
    isCancel: Axios.isCancel,
});
cancelableClient.interceptors.response.use(r => r, onError$2(cancelableClient));
cancelableClient.interceptors.response.use(r => r, onError$1(cancelableClient));
cancelableClient.interceptors.response.use(r => r, onError);
onRequestTokenUpdate(token => client.defaults.headers.requesttoken = token);

export { cancelableClient as default };
//# sourceMappingURL=index.esm.js.map
